import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useDebouncedValue } from '../../hooks';
import { Accordion } from '../accordion';
import { Button } from '../button';
import { ChecklistField, SearchField, useFormField } from '../forms';
import { Text } from '../text';

export interface AccordionMultiSelectItem {
  value: string;
  label: string;
}

export interface AccordionMultiSelectItemProps {
  title: string;
  iconName: IconName;
  items: AccordionMultiSelectItem[];
  fieldProps: any;
  selectAllLabel?: string;
  clearLabel?: string;
  emptyMessage?: string;
}

export interface AccordionMultiSelectProps {
  children: ReactNode;
  isMulti?: boolean;
}

const AccordionMultiSelectItem = ({
  title,
  iconName,
  items,
  fieldProps,
  selectAllLabel = 'Select All',
  clearLabel = 'Clear',
  emptyMessage = 'No results found',
}: AccordionMultiSelectItemProps) => {
  const searchField = useFormField({ type: 'text' });
  const debouncedSearch = useDebouncedValue(searchField.value, 300);

  const selectedValues = fieldProps.value ?? [];

  const filteredItems = debouncedSearch.trim()
    ? items.filter((item) => item.label.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : items;

  const isAllSelected = filteredItems.length > 0 && filteredItems.every((item) => selectedValues.includes(item.value));
  const isAnySelected = filteredItems.some((item) => selectedValues.includes(item.value));
  const selectionCount = selectedValues.length;

  const handleSelectAll = () => {
    const newValues = filteredItems.map((item) => item.value).filter((v) => !selectedValues.includes(v));
    if (newValues.length > 0) {
      fieldProps.onChange({ name: fieldProps.name, value: newValues });
    }
  };

  const handleClear = () => {
    const valuesToRemove = filteredItems
      .filter((item) => selectedValues.includes(item.value))
      .map((item) => item.value);
    fieldProps.onChange({ name: fieldProps.name, value: valuesToRemove });
  };

  return (
    <Accordion.Item value={fieldProps.name}>
      <Accordion.Header
        css={{
          '.header-container': {
            padding: theme.spacing(1.25, 2),
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        }}
      >
        <Accordion.Header.Icon title={title} icon={iconName} css={{ fontWeight: theme.font.weight.bold }} />
        {selectionCount > 0 && (
          <span
            css={{
              backgroundColor: theme.colors.primary10,
              color: theme.colors.neutral90,
              borderRadius: theme.borderRadius.small,
              fontSize: theme.fontSize(12),
              fontWeight: theme.font.weight.bold,
              minWidth: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.spacing(0, 0.5),
            }}
          >
            {selectionCount}
          </span>
        )}
      </Accordion.Header>
      <Accordion.Body style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
        <SearchField name={`${fieldProps.name}-search`} {...searchField} onKeyDown={(e) => e.stopPropagation()} />
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button variant='tertiary' disabled={isAllSelected} onClick={handleSelectAll}>
            {selectAllLabel}
          </Button>
          <Button variant='tertiary' disabled={!isAnySelected} onClick={handleClear}>
            {clearLabel}
          </Button>
        </div>
        {!filteredItems.length ? (
          <Text css={{ padding: theme.spacing(2), textAlign: 'center', color: theme.colors.neutral50 }}>
            {emptyMessage}
          </Text>
        ) : (
          <ChecklistField {...fieldProps} label='' showLabel={false} css={{ maxHeight: 200, overflowY: 'auto' }}>
            {filteredItems.map((item) => (
              <ChecklistField.Option key={item.value} name={item.value} css={checklistOptionStyle}>
                {item.label}
              </ChecklistField.Option>
            ))}
          </ChecklistField>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

const checklistOptionStyle = css({
  margin: 0,
  cursor: 'pointer',
  borderRadius: theme.borderRadius.small,
  alignItems: 'center',
  '> label': {
    flex: 1,
    cursor: 'pointer',
    padding: theme.spacing(1),
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  '> div': { margin: theme.spacing(1, 0, 1, 1) },
});

export const AccordionMultiSelect = ({ children, isMulti = false, ...rest }: AccordionMultiSelectProps) => (
  <Accordion variant='main' shouldRenderBody chevronSize={16} isMulti={isMulti} {...rest}>
    {children}
  </Accordion>
);

AccordionMultiSelect.Item = AccordionMultiSelectItem;
